import React from 'react';
import { observer } from 'owa-mobx-react';
/* eslint-disable-next-line @fluentui/react-components/prefer-fluentui-v9 -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE! Substitute this line with an explanation or resolve the problem by using v9 components instead
 *	> Avoid importing Panel from '@fluentui/react', as this package has started migration to Fluent UI 9. Import Drawer from '@fluentui/react-components' instead. */
import { Panel, PanelType, type IPanelStyles } from '@fluentui/react/lib/Panel';
import type { IOverlayProps } from '@fluentui/react/lib/Overlay';
import FolderPaneContainer from './FolderPaneContainer';
import {
    setShowFolderPane,
    setShowFolderPaneAsTransientOverlay,
    shouldShowFolderPane,
    shouldShowFolderPaneAsTransientOverlay,
    shouldShowFolderPaneOverlayAsBlocking,
} from 'owa-mail-layout';
import { getPalette } from 'owa-theme';
import { isShySuiteHeaderMode } from 'owa-suite-header-store';
import { APP_BAR_STATIC_WIDTH, APP_BAR_WITH_LABELS_STATIC_WIDTH } from 'owa-layout';
import { type StateChangeSource } from 'owa-left-pane-layout/lib/types/StateChangeSource';
import { customScrollBarOnHover } from 'owa-global-styles';
import { getOptionsForFeature, OwsOptionsFeatureType } from 'owa-outlook-service-option-store';
import type { AppBarDisplayModeOptions } from 'owa-outlook-service-option-store';
import isWin32StylingEnabled from 'owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled';
import classNames from 'owa-classnames';
import {
    overlayLeftPane,
    overlayLeftPaneBelowRibbon,
    overlayLeftPaneBelowRibbonWin32Overlay,
    overlayLeftPaneMain,
    overlayLeftPaneWithAppNames,
    isShyHeaderMode as shyModeMain,
    overlayLeftPaneScrollableContent,
    overlayLeftPaneScrollableContentNoScroll,
} from './MailLeftPaneAsOverlay.scss';

const leftRailWidth = 48;
const overlayLeftRailWidth = 280;

const folderPaneOverlayWidth = overlayLeftRailWidth - leftRailWidth;

const focusTrapZoneProps = {
    forceFocusInsideTrap: false,
    isClickableOutsideFocusTrap: true,
};

const defaultExport: React.FunctionComponent = observer(function MailLeftPaneAsOverlay() {
    const palette = getPalette();
    const isShyHeader = isShySuiteHeaderMode();
    const showWin32OverlayUI = isWin32StylingEnabled();
    const isAppNameLabelsEnabled = getOptionsForFeature<AppBarDisplayModeOptions>(
        OwsOptionsFeatureType.AppBarDisplayMode
    )?.labelsEnabled;
    const isTransientOverlayPane = shouldShowFolderPaneAsTransientOverlay();
    const overlayLeftPanePanelStyles = React.useMemo((): Partial<IPanelStyles> => {
        const folderPaneOverlayWidthString = `${folderPaneOverlayWidth}px`;
        const transientOverlayFlexColumnStyles = isTransientOverlayPane
            ? {
                  display: 'flex' as const,
                  flexDirection: 'column' as const,
                  height: '100%',
                  minHeight: 0,
              }
            : undefined;

        const transientOverlayContentStyles = isTransientOverlayPane
            ? {
                  ...transientOverlayFlexColumnStyles,
                  flex: '1 1 auto',
                  overflow: 'hidden' as const,
              }
            : undefined;

        return {
            main: [
                overlayLeftPaneMain,
                isShyHeader && shyModeMain,
                {
                    width: folderPaneOverlayWidthString,
                    // We need to move the panel 'underneath' the hamburger button since it's above the folder pane if it the OfficeRail rail is on
                    left:
                        '-' +
                        (isAppNameLabelsEnabled
                            ? APP_BAR_WITH_LABELS_STATIC_WIDTH
                            : APP_BAR_STATIC_WIDTH),
                },
            ],
            content: [
                {
                    width: folderPaneOverlayWidthString,
                    padding: 0,
                },
                transientOverlayFlexColumnStyles,
            ],
            contentInner: [
                {
                    background: palette.neutralLighter,
                },
                transientOverlayContentStyles,
            ],
            commands: {
                margin: 0,
                paddingTop: 0,
            },
            scrollableContent: [
                overlayLeftPaneScrollableContent,
                isTransientOverlayPane
                    ? overlayLeftPaneScrollableContentNoScroll
                    : customScrollBarOnHover,
                transientOverlayContentStyles,
            ],
        };
    }, [palette.neutralLighter, isShyHeader, isAppNameLabelsEnabled, isTransientOverlayPane]);

    const panelOverlayProps = React.useMemo((): IOverlayProps => {
        if (isTransientOverlayPane) {
            return {
                isDarkThemed: false,
                styles: {
                    root: {
                        backgroundColor: 'transparent',
                    },
                },
            };
        }

        return {
            isDarkThemed: false,
        };
    }, [isTransientOverlayPane]);

    return (
        <Panel
            className={classNames(
                isAppNameLabelsEnabled && overlayLeftPaneWithAppNames,
                overlayLeftPane,
                showWin32OverlayUI &&
                    isTransientOverlayPane &&
                    overlayLeftPaneBelowRibbonWin32Overlay,
                !showWin32OverlayUI && isTransientOverlayPane && overlayLeftPaneBelowRibbon
            )}
            styles={overlayLeftPanePanelStyles}
            isLightDismiss={true}
            isOpen={shouldShowFolderPane() || isTransientOverlayPane}
            hasCloseButton={false}
            overlayProps={panelOverlayProps}
            onOuterClick={isTransientOverlayPane ? onDismissFolderPaneOverlay : onOuterClick}
            focusTrapZoneProps={focusTrapZoneProps}
            onDismiss={onDismissFolderPaneOverlay}
            type={PanelType.smallFixedNear}
            isBlocking={isTransientOverlayPane || !shouldShowFolderPaneOverlayAsBlocking()}
        >
            <FolderPaneContainer />
        </Panel>
    );
}, 'MailLeftPaneAsOverlay');
export default defaultExport;

function onOuterClick() {
    // Need stub implemetation of onOuterClick to ensure this function is called instead of onDismiss
    // for the cases where the user clicks in the left rail area. Prevents the bug where a user clicks
    // the hamburger button and the pane collapses and immediately re expands.
    return;
}

function onDismissFolderPaneOverlay() {
    if (shouldShowFolderPaneAsTransientOverlay()) {
        setShowFolderPaneAsTransientOverlay(false);
        return;
    }

    setShowFolderPane(false, 0, false);
}
